'use client';

import { useEffect, useRef } from 'react';
import { SidebarActor, closeSidebar } from 'xstate/actors/SidebarActor';
import { SidebarView, StateValue } from 'xstate/machines/SidebarMachine';
import { usePathname } from 'core/i18n/navigation';
import { useSelector } from '@xstate/react';

interface UseSidebarResult {
  sidebarState: ReturnType<typeof SidebarActor.getSnapshot>;
  isClosed: boolean;
  isOpen: boolean;
  view: SidebarView;
}

export const useSidebar = (): UseSidebarResult => {
  const sidebarState = useSelector(SidebarActor, (snapshot) => snapshot);

  // State Helpers
  const isClosed = sidebarState.matches(StateValue.Closed);
  const isOpen = sidebarState.matches(StateValue.Open);

  // Context Helpers
  const view = sidebarState.context.view;

  // Width only — mobile browsers fire `resize` when the URL bar collapses on
  // scroll, which shouldn't close the drawer.
  useEffect(() => {
    if (!isOpen) return;

    const openedAtWidth = window.innerWidth;
    const handleResize = () => {
      if (window.innerWidth !== openedAtWidth) closeSidebar();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Close the drawer when navigating away, so it doesn't stay open across
  // pages. Compare paths rather than skipping the first run — the drawer's own
  // contents call this hook, so a mount isn't necessarily a navigation.
  const pathname = usePathname();
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    closeSidebar();
  }, [pathname]);

  return {
    sidebarState,
    isClosed,
    isOpen,
    view,
  };
};
